import React from "react";
import { GenericTableColumn } from "@ui-components/Table/GenericTable";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import { carrierBridgeAdminText } from "./carrierBridgeLabels";

/**
 * A Carrier Bridge as the admin endpoint returns it
 * (`GET /v1/resource-manager/ke-carrier-bridge`, chopper-backend CTX-714).
 */
export interface AdminCarrierBridgeResponse {
  id: number;
  subscriptionId: string | null;
  name: string | null;
  tenantId: number | null;
  kontrolEdgePortId: number | null;
  kontrolEdgeLagId: number | null;
  /** Null means the whole port goes to the carrier. */
  vlan: string | null;
  mbps: number;
  carrierId: number | null;
  circuitId: string | null;
  isDraft: boolean;
  tag: string[] | null;
  kontrolEdgePort?: {
    id: number;
    subid: string | null;
    name: string | null;
    tenantId: number | null;
    datacenterId: string | null;
    capacityMbps: number | null;
  } | null;
  tenant?: { id: number; Name: string | null; nameDisplay: string | null } | null;
  carrier?: { id: number; name: string; code: string | null } | null;
  kontrolEdgeLag?: { id: number; name: string | null } | null;
}

/** One row of the admin table: display-ready strings plus the raw record. */
export interface CarrierBridgeRow {
  id: number;
  serviceId: string;
  name: string;
  isDraft: boolean;
  tenantName: string;
  portSubid: string;
  vlanLabel: string;
  carrierName: string;
  mbps: number;
  raw: AdminCarrierBridgeResponse;
}

const NO_VALUE = "-";

export function shapeCarrierBridgeRows(
  services: AdminCarrierBridgeResponse[]
): CarrierBridgeRow[] {
  return services.map((service) => ({
    id: service.id,
    serviceId: service.subscriptionId ?? NO_VALUE,
    name: service.name ?? NO_VALUE,
    isDraft: service.isDraft,
    tenantName:
      service.tenant?.nameDisplay || service.tenant?.Name || NO_VALUE,
    portSubid: service.kontrolEdgePort?.subid ?? NO_VALUE,
    // A null VLAN is the whole port; the approved design shows it as "-".
    vlanLabel: service.vlan ?? carrierBridgeAdminText.noVlan,
    carrierName: service.carrier?.name ?? NO_VALUE,
    mbps: service.mbps,
    raw: service,
  }));
}

export const carrierBridgeColumns: GenericTableColumn<CarrierBridgeRow>[] = [
  { header: "Id", accessor: "serviceId" },
  { header: "Name", accessor: "name" },
  {
    header: "Status",
    accessor: "isDraft",
    sortAccessor: (row) => (row.isDraft ? 1 : 0),
    renderCell: (row) =>
      row.isDraft ? (
        <GenericTag
          label={carrierBridgeAdminText.status.draft}
          variant="warning"
          size="small"
        />
      ) : (
        <GenericTag
          label={carrierBridgeAdminText.status.active}
          variant="success"
          size="small"
        />
      ),
  },
  { header: "Tenant", accessor: "tenantName" },
  { header: "Port", accessor: "portSubid" },
  { header: "VLAN", accessor: "vlanLabel" },
  { header: "Carrier", accessor: "carrierName" },
  { header: "Mbps", accessor: "mbps" },
];
